import { initGoogleAnalytics } from '../analytics/googleAnalytics';

export const COOKIE_CONSENT_KEY = 'zl-cookie-consent';
export const COOKIE_SETTINGS_EVENT = 'zl-open-cookie-settings';

/** Opens the cookie banner again so a previous choice can be changed. */
export function openCookieSettings(): void {
  window.dispatchEvent(new Event(COOKIE_SETTINGS_EVENT));
}

export type CookieConsent = 'accepted' | 'declined';

export function getCookieConsent(): CookieConsent | null {
  try {
    const value = localStorage.getItem(COOKIE_CONSENT_KEY);
    if (value === 'accepted' || value === 'declined') return value;
  } catch {
    /* private browsing / blocked storage */
  }
  return null;
}

export function setCookieConsent(value: CookieConsent): void {
  try {
    localStorage.setItem(COOKIE_CONSENT_KEY, value);
  } catch {
    /* noop */
  }
  if (value === 'accepted') loadGoogleAfterConsent();
  else revokeGoogleConsent();
}

/** Loads Google only when a previous visit already accepted. Decline stays silent. */
export function restoreConsentFromStorage(): void {
  if (getCookieConsent() === 'accepted') loadGoogleAfterConsent();
}

/**
 * Ads conversions are the existing tags inside GTM-P64DV6TH:
 * form H_xFCL-8itIcEPGIwpxE on submit, phone j8CtCOHDitIcEPGIwpxE on tel: clicks,
 * email m7KHCO3JitIcEPGIwpxE on mailto: clicks.
 * Do not send those labels again from the page.
 */
export const FORM_SUBMIT_EVENT = 'zl_form_submit';
export const PHONE_CLICK_EVENT = 'zl_phone_click';

const GTM_ID = 'GTM-P64DV6TH';

let googleLoaded = false;

function ensureGtagStub(): void {
  window.dataLayer = window.dataLayer ?? [];
  if (typeof window.gtag !== 'function') {
    window.gtag = function gtag() {
      window.dataLayer!.push(arguments as unknown);
    };
  }
}

function pushConsent(granted: boolean): void {
  ensureGtagStub();
  const state = granted ? 'granted' : 'denied';
  window.gtag!('consent', 'default', {
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
    analytics_storage: 'denied',
  });
  window.gtag!('consent', 'update', {
    ad_storage: state,
    ad_user_data: state,
    ad_personalization: state,
    analytics_storage: state,
  });
}

/** Consent Mode first, then Tag Manager and Analytics. Never call this before accept. */
export function loadGoogleAfterConsent(): void {
  pushConsent(true);
  if (googleLoaded) return;
  googleLoaded = true;

  window.dataLayer!.push({ 'gtm.start': Date.now(), event: 'gtm.js' });
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtm.js?id=${GTM_ID}`;
  document.head.appendChild(script);
  initGoogleAnalytics();
}

/** Stops further Google use after a later decline. Does not load anything if nothing was loaded. */
export function revokeGoogleConsent(): void {
  if (!googleLoaded) return;
  pushConsent(false);
}

export function pushConversionEvent(eventName: string): void {
  if (getCookieConsent() !== 'accepted') return;
  window.dataLayer = window.dataLayer ?? [];
  window.dataLayer.push({ event: eventName });
}
